import React from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import { isNull } from 'lodash';

import Input from '../../../Input';
import ListSearchCard from '../ListSearchCard';
import { SEARCH_CARD_TYPES } from '../../../../constants';
import OnClickOutside from '../../../OnClickOutside';
import Button from '../../../Button';
import {
  IClientSearchInfo,
  ICommunicationSearchInfo,
  INoteSearchInfo,
  IOrderSearchInfo,
  ISearchCardActions,
  SearchCardType,
} from '../types';

const PAGE_SIZE_DEFAULT = 20;
const THRESHOLD_DEFAULT = 5;
const QUANTITY_FOR_REQUST_DEFAULT = 1;

export interface SearchCardsDropdownProps {
  className?: string;
  placeholder?: string;
  minimumBatchSize?: number;
  threshold?: number;
  quantity?: number;
  displayCount?: number;
  hasNextPage?: boolean;
  clients?: {
    items: IClientSearchInfo[];
    total: number;
  };
  orders?: {
    items: IOrderSearchInfo[];
    total: number;
  };
  communications?: {
    items: ICommunicationSearchInfo[];
    total: number;
  };
  notes?: {
    items: INoteSearchInfo[];
    total: number;
  };
  messages?: {
    items: any[]; // хз, что тут должно быть, поскольку бэк ничего не присылает))
    total: number;
  };
  isLoadingClients: boolean;
  isLoadingOrders: boolean;
  isLoadingCommunications: boolean;
  isLoadingNotes: boolean;
  isLoadingMessages: boolean;

  onToggle: ISearchCardActions['onToggle'];
  onLoadClientsRequest: ISearchCardActions['onLoadRequest'];
  onLoadOrdersRequest: ISearchCardActions['onLoadRequest'];
  onLoadCommunicationsRequest: ISearchCardActions['onLoadRequest'];
  onLoadNotesRequest: ISearchCardActions['onLoadRequest'];
  onLoadMessagesRequest: ISearchCardActions['onLoadRequest'];
  onCallClick: ISearchCardActions['onCallClick'];
  onEmailClick: ISearchCardActions['onEmailClick'];
  onCreateTicket: ISearchCardActions['onCreateTicket'];
  onCardClick: ISearchCardActions['onCardClick'];

  onChange: (value: string) => void;
  onSearch: ({ value }: { value: string }) => void;
}

interface SearchCardsDropdownState {
  type: SearchCardType | null;
  value: string;
  isOpen: boolean;
}

class SearchCardsDropdown extends React.PureComponent<
  SearchCardsDropdownProps,
  SearchCardsDropdownState
> {
  private ref: React.RefObject<HTMLDivElement>;

  static defaultProps = {
    placeholder: '',
    minimumBatchSize: PAGE_SIZE_DEFAULT,
    threshold: THRESHOLD_DEFAULT,
    quantity: QUANTITY_FOR_REQUST_DEFAULT,
    displayCount: 3,
    hasNextPage: true,
    clients: { items: [], total: 0 },
    communications: { items: [], total: 0 },
    notes: { items: [], total: 0 },
    orders: { items: [], total: 0 },
    messages: { items: [], total: 0 },
    onLoadCommunicationsRequest: Function.prototype,
    onLoadNotesRequest: Function.prototype,
    onLoadOrdersRequest: Function.prototype,
    onLoadClientsRequest: Function.prototype,
    onLoadMessagesRequest: Function.prototype,
    onToggle: Function.prototype,
    onSearch: Function.prototype,
    onCallClick: Function.prototype,
    onEmailClick: Function.prototype,
    onCreateTicket: Function.prototype,
    onCardClick: Function.prototype,
    onChange: Function.prototype,
    isLoadingOrders: false,
    isLoadingClients: false,
    isLoadingCommunications: false,
    isLoadingNotes: false,
    isLoadingMessages: false,
  };

  constructor(props) {
    super(props);

    this.state = {
      type: null,
      value: '',
      isOpen: false,
    };

    this.ref = React.createRef();
    this.handleToggle = this.handleToggle.bind(this);
    this.handleChange = this.handleChange.bind(this);
    this.handleKeyDown = this.handleKeyDown.bind(this);
    this.handleClick = this.handleClick.bind(this);
    this.handleCardClick = this.handleCardClick.bind(this);
    this.handleClickOutside = this.handleClickOutside.bind(this);
  }

  handleCardClick(data) {
    const { onCardClick } = this.props;
    this.setState(() => ({
      isOpen: false,
    }));
    this.setState(() => ({
      value: '',
      type: null,
    }));
    onCardClick(data);
  }

  handleSubmit() {
    const { onSearch } = this.props;
    const { value } = this.state;
    onSearch({ value });
  }

  handleClick(event) {
    event.stopPropagation();

    const { value } = this.state;
    const { quantity } = this.props;

    if (!!quantity && value.length >= quantity) {
      this.setState(() => ({
        isOpen: true,
      }));
    }
  }

  handleClickOutside() {
    this.setState(() => ({
      isOpen: false,
    }));
  }

  handleKeyDown({ key }) {
    if (key === 'Escape') {
      this.setState(() => ({
        isOpen: false,
      }));
    } else if (key === 'Enter') {
      this.handleSubmit();
    }
  }

  handleChange({ target }) {
    const { onChange } = this.props;
    const { value } = target;
    this.setState(() => ({ value }));
    this.setState(() => ({
      isOpen: value !== '',
    }));
    onChange(value);
  }

  handleToggle(newType) {
    const { onToggle } = this.props;
    const { type } = this.state;
    const result = isNull(type) ? newType : null;
    this.setState(
      {
        type: result,
      },
      () => onToggle(result)
    );
  }

  get headRenderer() {
    const { value } = this.state;
    const { placeholder } = this.props;

    return (
      <StyledInputSearchCards
        placeholder={placeholder}
        value={value}
        onChange={this.handleChange}
        onKeyDown={this.handleKeyDown}
        onClick={this.handleClick}
      />
    );
  }

  get contentRenderer() {
    const { type } = this.state;
    const {
      className,
      clients,
      orders,
      communications,
      notes,
      messages,
      onLoadOrdersRequest,
      onLoadClientsRequest,
      onLoadCommunicationsRequest,
      onLoadNotesRequest,
      onLoadMessagesRequest,
      minimumBatchSize,
      threshold,
      displayCount,
      hasNextPage,
      onCallClick,
      onEmailClick,
      onCreateTicket,
      isLoadingClients,
      isLoadingOrders,
      isLoadingCommunications,
      isLoadingNotes,
      isLoadingMessages,
    } = this.props;
    const isOpen = !!type;
    let action = {
      onToggle: this.handleToggle,
      onCardClick: this.handleCardClick,
    } as any;
    return (
      <StyledSearchCards className={className}>
        {Object.values(SEARCH_CARD_TYPES).map(componentType => {
          let data;

          switch (componentType) {
            case SEARCH_CARD_TYPES.CLIENTS:
              data = {
                ...clients,
                isLoading: isLoadingClients,
              };
              action = {
                ...action,
                onLoadRequest: onLoadClientsRequest,
                onCallClick,
                onEmailClick,
                onCreateTicket,
              };
              break;
            case SEARCH_CARD_TYPES.ORDERS:
              data = {
                ...orders,
                isLoading: isLoadingOrders,
              };
              action = {
                ...action,
                onLoadRequest: onLoadOrdersRequest,
                onCreateTicket,
              };
              break;
            case SEARCH_CARD_TYPES.COMMUNICATION:
              data = {
                ...communications,
                isLoading: isLoadingCommunications,
              };
              action = {
                ...action,
                onLoadRequest: onLoadCommunicationsRequest,
              };
              break;
            case SEARCH_CARD_TYPES.NOTES:
              data = {
                ...notes,
                isLoading: isLoadingNotes,
              };
              action = {
                ...action,
                onLoadRequest: onLoadNotesRequest,
              };
              break;
            // TODO Выключили в рамках задачи LT-46118, вернуть как будет готов бэк
            // case SEARCH_CARD_TYPES.MESSAGES:
            //   data = {
            //     ...messages,
            //     isLoading: isLoadingMessages,
            //   };
            //   action = {
            //     ...action,
            //     onLoadRequest: onLoadMessagesRequest,
            //   };
            //   break;
            default:
              data = null;
              break;
          }
          return (
            data &&
            this.isDisplay(componentType) && (
              <ListSearchCard
                key={componentType}
                type={componentType}
                total={data.total}
                items={data.items}
                isLoading={data.isLoading}
                action={action}
                minimumBatchSize={minimumBatchSize}
                threshold={threshold}
                open={isOpen}
                hasNextPage={hasNextPage}
                displayCount={displayCount}
              />
            )
          );
        })}
      </StyledSearchCards>
    );
  }

  isDisplay(componentType) {
    const { type } = this.state;
    // Если не выбран тип => отображаем все компоненты
    if (isNull(type)) {
      return true;
    }
    // Иначе отображаем только тот компонент, который выбран
    return componentType === type;
  }

  render() {
    const { isOpen } = this.state;
    return (
      <OnClickOutside onClickOutside={this.handleClickOutside}>
        <StyledHeader functional onClick={this.handleClick}>
          {this.headRenderer}
        </StyledHeader>
        {isOpen && (
          <StyledContentContainer ref={this.ref}>
            {this.contentRenderer}
          </StyledContentContainer>
        )}
      </OnClickOutside>
    );
  }
}

export const StyledInputSearchCards = styled(Input)`
  position: relative;
  z-index: 10;
  width: 100%;
  height: 40px;
  font-size: 13px;
  border-radius: 4px;
  border: solid 1px #c2c5cc;

  &::-webkit-outer-spin-button,
  &::-webkit-inner-spin-button {
    -webkit-appearance: none;
    margin: 0;
  }
`;

const StyledHeader = styled(Button)`
  width: 100%;
`;

const StyledSearchCards = styled.div`
  width: 100%;
  height: auto;
  background-color: #fff;
  border: 1px;
  border-radius: 6px;
  display: flex;
  flex-direction: column;
`;

const StyledContentContainer = styled.div`
  position: relative;
`;

export default SearchCardsDropdown;
